import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { canReach } from '../nav'
import { navSubjectFrom } from '../navSubject'
import { useSession } from '../useSession'

/**
 * Gates a route on a navigation destination (D-322).
 *
 * **The same function the rail calls.** `RequireCapability` cannot express these
 * routes — Schedule is gated on having days here or having a child here, and
 * neither is a capability. Written out again in the router, the two would
 * disagree eventually, and the disagreement shows up either as a rail item that
 * refuses or as a URL that works while nothing offers it. The second is worse,
 * because nobody is looking for it.
 *
 * D-261 already paid for this: two implementations of "is this public" drifted,
 * and the fix was one function that both the catalog and the explanation read.
 *
 * **Convenience only, exactly like {@link RequireCapability}.** The server
 * decides (D-73); every endpoint behind these screens is independently checked.
 * This stops a person wandering into a screen that would only tell them no.
 */
export default function RequireDestination({
  destination,
  children,
}: {
  destination: string
  children: ReactNode
}) {
  const { session, isLoading } = useSession()

  // Nothing while it is in flight: "not loaded yet" and "not allowed" are the
  // same value until the request lands, and refusing early flashes a denial at
  // somebody who is allowed.
  if (isLoading) return null

  if (session && canReach(navSubjectFrom(session), destination)) return <>{children}</>

  /*
   * Says no rather than redirecting, the same choice D-176 made for
   * `RequireCapability`: bouncing somebody to the calendar reads as the link
   * being broken, and the next thing that happens is a message to Tonya.
   */
  return (
    <div className="mx-auto w-full max-w-[34rem] px-5 py-16 text-center">
      <div className="mx-auto grid size-11 place-items-center rounded-xl bg-surface-2 text-ink-3">
        <Lock size={19} strokeWidth={2} />
      </div>
      <h1 className="mt-4 font-display text-xl">Not your page</h1>
      <p className="mt-2 text-[13.5px] text-ink-3">
        This one is not part of your studio account. If you think it should be, ask Tonya.
      </p>
    </div>
  )
}
